/**
 * HeroSection — centered brutalist hero, rebuilt to FAITHFULLY match the Figma
 * (node 1:580) and adapted to a LIGHT page + StudySync brand.
 *
 * Figma → StudySync mapping (structure preserved):
 *   "BUILD DEBUG / DEPLOY FASTER"       → "MAKING HEALTH DATA / COLLECTION SIMPLE"
 *                                         (last word in Cardinal accent)
 *   2-line muted subhead                → StudySync infrastructure subhead
 *   primary CTA w/ layered colored      → RetroUI Button w/ shadow-brand
 *     offset shadow                       (Teal+Cardinal offset stack)
 *   outline secondary CTA               → RetroUI outline Button
 *
 * Motion: framer-motion entrance reveal (reduced-motion aware).
 * ZERO MUI.
 */
import * as React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import type { Variants } from 'framer-motion';
import { Button } from '../retroui';
import { useContactDialog } from './ContactDialogProvider';
import HeroFloatingMetrics from './HeroFloatingMetrics';

/* ────────────────────────────────────────────────────────────────────────────
 * Entrance reveal variants (reduced-motion aware via a parent gate).
 * ──────────────────────────────────────────────────────────────────────────── */
const container: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};
const item: Variants = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

/** Height of the sticky nav (matches LandingNav's `h-[88px]`) — the in-page scroll
 *  subtracts it so the "How it works" heading clears the bar instead of hiding behind it. */
const NAV_HEIGHT = 88;

const HeroSection: React.FC = () => {
  const reduceMotion = useReducedMotion();
  // Shared contact dialog (owned by ContactDialogProvider). The primary CTA opens it
  // tagged as the 'talk-to-us' source — same dialog the final "Schedule a demo" uses.
  const { open: openContact } = useContactDialog();

  // When reduced motion is preferred, render statically (no initial/animate).
  const motionProps = reduceMotion
    ? {}
    : {
        variants: container,
        initial: 'hidden' as const,
        animate: 'visible' as const,
      };
  const itemMotion = reduceMotion ? {} : { variants: item };

  /** Smooth-scroll the "See how it works" CTA to the SetupSection (#setup), offset
   *  for the sticky nav. Mirrors LandingNav's anchor behavior; falls back to the
   *  browser default if the section isn't on the page. */
  const handleSeeHowItWorks = React.useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      const target = document.getElementById('setup');
      if (!target) return;
      e.preventDefault();
      const top = target.getBoundingClientRect().top + window.scrollY - NAV_HEIGHT;
      window.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
      window.history.pushState(null, '', '#setup');
    },
    [reduceMotion],
  );

  return (
    <section
      id="top"
      className="relative flex flex-col justify-center overflow-hidden bg-background px-4 pb-12 pt-16 md:px-8 md:pb-6 md:pt-10 lg:flex-1"
    >
      {/* Decorative floating health-metric tiles in the side gutters (lg+ only). */}
      <HeroFloatingMetrics />

      {/* Centered headline column. */}
      <motion.div className="relative z-10 mx-auto max-w-4xl text-center" {...motionProps}>
        {/* Eyebrow badge — flat outlined pill in StudySync Teal (2px black border,
            no shadow, no tilt). Static copy; renders immediately (no count-up).
            Pairs with the headline's Cardinal accent word. */}
        <motion.div {...itemMotion} className="mb-14 md:mb-12">
          <span className="inline-block border-2 border-solid border-black bg-secondary px-7 py-3 font-head text-2xl tracking-tight text-secondary-foreground sm:text-3xl md:text-4xl">
            250+ Available Health Metrics
          </span>
        </motion.div>

        {/* Headline — huge uppercase, last word in Cardinal (Figma's accent word). */}
        <motion.h1
          {...itemMotion}
          className="font-head text-4xl uppercase leading-[0.95] tracking-tight text-foreground sm:text-5xl md:text-6xl lg:text-7xl"
        >
          MAKING HEALTH DATA
          <br />
          COLLECTION <span className="text-primary">SIMPLE</span>
        </motion.h1>

        {/* Subhead — muted, centered, two-ish lines (Figma). */}
        <motion.p
          {...itemMotion}
          className="mx-auto mt-7 max-w-2xl font-sans text-lg text-muted-foreground md:mt-5"
        >
          StudySync is the infrastructure for large-scale wearable + EHR research —
          acquisition, storage, and per-participant export, with minimum
          configuration.
        </motion.p>

        {/* CTAs — primary with layered brand offset shadow, outline secondary. */}
        <motion.div
          {...itemMotion}
          className="mt-10 flex flex-col items-center justify-center gap-5 sm:flex-row md:mt-7"
        >
          <Button
            type="button"
            size="lg"
            onClick={() => openContact('talk-to-us')}
            className="w-full shadow-brand hover:shadow-brand active:translate-y-1 sm:w-auto"
          >
            <span className="font-sans text-base font-bold text-primary-foreground">
              TELL US ABOUT YOUR STUDY
            </span>
          </Button>
          {/* Secondary CTA — the framed (transparent fill, black outline, no rest
              shadow) variant adapted to the LIGHT theme. Sized to MATCH the primary
              "TELL US ABOUT YOUR STUDY" box (ignoring that button's offset shadow):
              `border-2` overrides the variant's 3px edge, and the label uses the SAME
              `font-sans text-base font-bold` treatment as the primary span with
              `tracking-normal` (cancelling the variant's wide letter-spacing) — only
              the color differs (BLACK `text-foreground`). All caps. */}
          <Button
            variant="framed"
            size="lg"
            className="w-full border-2 sm:w-auto"
            render={
              <a href="#setup" onClick={handleSeeHowItWorks}>
                <span className="font-sans text-base font-bold tracking-normal text-foreground">
                  SEE HOW IT WORKS
                </span>
              </a>
            }
          />
        </motion.div>

        {/* Participant-app download affordance — a distinct row BELOW the CTAs.
            Uses the OFFICIAL Apple "Download on the App Store" badge artwork
            (self-contained pill with its own black fill, rounded corners, and
            border), so the wrapping link adds no border/background/shadow of its
            own. Same framer-motion entrance (reduced-motion-safe via
            `itemMotion`). On MOBILE the badge spans the full content width; sm+
            restores the fixed-height (44px) badge. */}
        <motion.div
          {...itemMotion}
          className="mt-5 flex flex-col items-center"
        >
          <a
            href="https://apps.apple.com/au/app/studysync-snyder-lab/id6756796122"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download StudySync on the App Store"
            className="mt-2 block w-full no-underline transition hover:opacity-90 sm:inline-block sm:w-auto"
          >
            <img
              src="/landing/app-store-badge.svg"
              alt="Download on the App Store"
              className="h-auto w-full sm:h-[54px] sm:w-auto"
            />
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
